import Link from 'next/link'

import { Poster } from '@/app/components/atoms/Poster'
import { Price } from '@/app/components/atoms/Price'
import { PriceTag } from '@/app/components/atoms/PriceTag'
import { Sticker } from '@/app/components/atoms/Sticker'
import { Tag } from '@/app/components/atoms/Tag'
import { cx } from '@/app/lib/utils'

import type { ProductGridCardData } from './utils/toGridCards'
import styles from './ProductCard.module.scss'

// `id` es solo la clave de lista del padre (`ProductGrid.tsx`), no un dato
// que la tarjeta necesite renderizar — se excluye de las props.
interface ProductCardProps extends Omit<ProductGridCardData, 'id'> {
  readonly className?: string
}

/**
 * Tarjeta del módulo «Productos3Columnas» — imagen 1:1 + fecha/título/ciudad/
 * precio, SIN botón de compra propio: toda la tarjeta es un único `<Link>` a
 * la ficha del producto (mismo patrón que `OtrosProductosIzqCard`, specs/029).
 *
 * Card **responsive única**, no dos variantes por breakpoint: en móvil/tablet
 * se apila (imagen arriba, cuerpo debajo); desde escritorio pasa a apaisada
 * (imagen a la izquierda, cuerpo a la derecha) solo por CSS
 * (`ProductCard.module.scss`, `flex-direction: column` → `row`) — un único
 * árbol de imagen por producto, no duplicado por breakpoint (plan.md Summary
 * punto 1).
 *
 * Presentacional pura (sin estado, efectos ni lógica propia más allá de la
 * precedencia insignia-de-estado/insignia-de-descuento, ya resuelta por
 * `mapProductToCard`/`resolvePurchaseState`) → Server Component. Renderizado
 * defensivo: imagen, ciudad, precio y categoría se omiten si no hay dato.
 *
 * Insignia de estado (`imageBadge`) y etiqueta de categoría (`tag`) son
 * mutuamente excluyentes sobre la imagen — cuando el producto no está
 * disponible para compra, la insignia sustituye a la categoría; la insignia
 * de descuento (`PriceTag`) tampoco se muestra junto a la de estado (FR-008,
 * FR-009). `accentColor` en `Price`: el importe rebajado se pinta en negro,
 * paridad visual con la referencia (`ProductCardVertical`/`CardHorizontal`
 * de `eci-firefly-uikit`, research.md §6). El badge de descuento (`PriceTag`)
 * es siempre rojo (`--color-promo`), sin variante — misma insignia en toda
 * la web.
 */
export function ProductCard({
  title,
  href,
  img,
  date,
  venue,
  priceDisplay,
  tag,
  sticker,
  imageBadge,
  className,
}: ProductCardProps) {
  return (
    <article className={cx(styles.card, className)}>
      <Link href={href} className={styles.link}>
        <div className={cx(styles.imageWrap, imageBadge && styles.imageWrapMuted)}>
          {img && <Poster src={img} alt="" className={styles.poster} />}

          {tag && !imageBadge && (
            <Tag variant="black" className={styles.tagTopLeft}>
              {tag}
            </Tag>
          )}

          <PriceTag
            tag={priceDisplay?.tag ?? null}
            hasAppPromotions={priceDisplay?.hasAppPromotions}
            className={styles.tagTopRight}
          />

          {imageBadge && (
            <div className={styles.imageBadge}>
              <Tag variant="status">{imageBadge}</Tag>
            </div>
          )}

          {/* Sticker comercial — esquina inferior izquierda de la imagen. Se
              oculta con estado de compra (igual que la categoría): un evento
              no comprable no promociona "Últimas entradas". */}
          {sticker && !imageBadge && (
            <div className={styles.stickerBottomLeft}>
              <Sticker>{sticker}</Sticker>
            </div>
          )}
        </div>

        <div className={styles.body}>
          {date && <p className={styles.date}>{date}</p>}
          {/* h2, no h3: el título del módulo (`Productos3Columnas.tsx`), cuando
              existe, también es un `<h2>` — la página anfitriona no intercala
              ningún h2 entre su propio h1 y este módulo (mismo criterio que
              `OtrosProductosIzqCard`, specs/029). */}
          <h2 className={styles.title}>{title}</h2>
          {venue && <p className={styles.venue}>{venue}</p>}

          {priceDisplay && (
            <Price
              amount={priceDisplay.amount}
              isFree={priceDisplay.isFree}
              originalAmount={priceDisplay.originalAmount ?? undefined}
              variant={priceDisplay.originalAmount != null ? 'offer' : 'default'}
              showFrom
              accentColor
              className={styles.price}
            />
          )}
        </div>
      </Link>
    </article>
  )
}
